import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useDragReorder from "@/lib/useDragReorder";

const empty = { question: "", answer: "", sort_order: 0, is_active: true };

export default function AdminFaqs() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/faqs").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...f }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
    if (editing) await api.patch(`/admin/faqs/${editing.id}`, payload);
    else await api.post("/admin/faqs", payload);
    toast.success(editing ? "Updated" : "Created");
    setOpen(false); load();
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/faqs/${id}`); load(); };

  const drag = useDragReorder({
    items,
    getId: (f) => f.id,
    onReorder: async (newList, ids) => {
      setItems(newList);
      try { await api.post("/admin/faqs/reorder", { ids }); toast.success("Order updated"); }
      catch { toast.error("Failed to save order"); load(); }
    },
  });

  return (
    <div className="space-y-6" data-testid="admin-faqs">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-semibold text-slate-900">FAQs</h1><p className="text-sm text-slate-500">Drag to reorder.</p></div>
        <Button onClick={openNew} className="bg-primary hover:bg-primary/90" data-testid="faq-new"><Plus className="mr-1 h-4 w-4" />New FAQ</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <ul className="divide-y">
          {items.map((f) => (
            <li
              key={f.id}
              {...drag.rowProps(f)}
              className={`flex items-start justify-between gap-4 p-4 transition-colors ${drag.rowClass(f) || "hover:bg-slate-50"}`}
              data-testid={`faq-row-${f.id}`}
            >
              <div className="flex min-w-0 flex-1 items-start gap-2">
                <div className="pt-0.5"><drag.Handle testId={`faq-row-${f.id}-handle`} /></div>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">{f.question}</div>
                  <div className="mt-1 text-sm text-slate-600 line-clamp-2">{f.answer}</div>
                  <div className="mt-1 text-xs text-slate-400">{f.is_active ? "Active" : "Hidden"}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(f)} data-testid={`faq-edit-${f.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(f.id)} data-testid={`faq-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
              </div>
            </li>
          ))}
          {items.length === 0 && <li className="p-10 text-center text-slate-400">No FAQs.</li>}
        </ul>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit FAQ" : "New FAQ"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="faq-form">
            <div><Label>Question</Label><Input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} /></div>
            <div><Label>Answer</Label><Textarea required rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} /></div>
            <div className="flex items-end gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <span className="text-sm">{form.is_active ? "Active" : "Hidden"}</span>
            </div>
            <DialogFooter><Button type="submit" className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
